import { action, computed, observable, runInAction } from 'mobx';
import { ISelectOption, TSelectValue } from 'telia-front-react';
import config from '../Config';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';

export interface EntryInformationFormData {
  personEmail?: string;
  personPhone?: string;
  personPersonalCode?: string;
  personFirstName?: string;
  personLastName?: string;
  personMobilePhone?: string;
  argosClientId?: string;
  customerReferenceNumberId?: string;
  customerReferenceNumber?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerRegistrationCode?: string;
  customerAddress?: string;
  customerDebt?: string;
}

export interface VkInfo {
  vkDate?: string;
  vkTime?: string;
  vkMac?: string;
}
export interface EntryWithVkInfo {
  formData?: EntryInformationFormData;
  vkInfo?: VkInfo;
}

export interface ReferenceNumberInfo {
  refNumber?: string;
  accountId?: number;
  address?: string;
  customerId?: number;
}

interface EntryInformationItemsHolder {
  formDataList?: EntryWithVkInfo[];
  referenceNumbers?: ReferenceNumberInfo[];
  status?: FetchStatus;
}

interface FormData {
  taotluse_esitaja_email: string;
  taotluse_esitaja_telefon: string;
  taotluse_esitaja_isikukood: string;
  taotluse_esitaja_nimi: string;
  taotluse_esitaja_eesnimi: string;
  taotluse_esitaja_mobiil: string;
  argose_kliendi_nr: string;
  taotleja_teletunnus_id: string;
  taotleja_eesnimi: string;
  taotleja_nimi: string;
  taotleja_registri_isikukood: string;
  taotleja_aadress: string;
  kliendi_volg: string;
  taotleja_teletunnus: string;
  VK_DATE: string;
  VK_TIME: string;
  VK_MAC: string;
  [key: string]: any;
}

export enum LinkType {
  GEODEET = 'geodeet',
  TEHNILINE = 'tehniline',
  KOOSKOLASTAMINE = 'kooskolastamine',
  JARELVALVE = 'jarelvalve',
}

class ConstructorPortalStore {
  @observable accessor entryInformationHolder: EntryInformationItemsHolder = {
    status: FetchStatus.UNINITIALIZED,
  };
  @observable accessor selectedAddressOption: ISelectOption = undefined;
  @observable accessor showNoAddressNotice: boolean = false;

  constructor() {}

  @computed
  public get isEntryInformationFetching() {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.entryInformationHolder.status);
  }

  @computed
  public get addressOptions() {
    const options: ISelectOption[] = [];
    this.entryInformationHolder?.referenceNumbers?.forEach((referenceNumber) => {
      options.push({
        value: referenceNumber.refNumber,
        label: `${referenceNumber.address} ${referenceNumber.refNumber}`,
      });
    });
    return options;
  }

  handleAddressSelection = (value: TSelectValue) => {
    this.setSelectedAddressOption(value as ISelectOption);
  };

  @action
  private setSelectedAddressOption(value: ISelectOption): void {
    this.selectedAddressOption = value;
  }

  private get entryWithVkInfo(): EntryWithVkInfo {
    const referenceAccountMap = new Map(this.entryInformationHolder.referenceNumbers.map((ref) => [ref.refNumber, ref.accountId]));

    const accountId = referenceAccountMap.get(this.selectedAddressOption.value);

    return this.entryInformationHolder.formDataList.find((entryWithVk) => Number(entryWithVk.formData.customerReferenceNumberId) === accountId);
  }

  public postData = async (type: LinkType) => {
    runInAction(() => {
      this.entryInformationHolder.status = FetchStatus.FETCHING;
    });
    const result = await API.get('/construction-portal/entry-information');
    runInAction(() => {
      this.entryInformationHolder = result.data;
    });
    const { formData, vkInfo } = this.entryWithVkInfo;

    console.log('formData', formData);

    const data = {
      taotluse_esitaja_email: formData.personEmail ? formData.personEmail : '',
      taotluse_esitaja_telefon: formData.personPhone ? formData.personPhone : '',
      taotluse_esitaja_isikukood: formData.personPersonalCode ? formData.personPersonalCode : '',
      taotluse_esitaja_nimi: formData.personLastName ? formData.personLastName : '',
      taotluse_esitaja_eesnimi: formData.personFirstName ? formData.personFirstName : '',
      taotluse_esitaja_mobiil: formData.personMobilePhone ? formData.personMobilePhone : '',
      argose_kliendi_nr: formData.argosClientId ? formData.argosClientId : '',
      taotleja_teletunnus_id: formData.customerReferenceNumberId ? formData.customerReferenceNumberId : '',
      taotleja_eesnimi: formData.customerFirstName ? formData.customerFirstName : '',
      taotleja_nimi: formData.customerLastName ? formData.customerLastName : '',
      taotleja_registri_isikukood: formData.customerRegistrationCode ? formData.customerRegistrationCode : '',
      taotleja_aadress: formData.customerAddress ? formData.customerAddress : '',
      kliendi_volg: formData.customerDebt ? formData.customerDebt : '',
      taotleja_teletunnus: formData.customerReferenceNumber ? formData.customerReferenceNumber : '',
      VK_DATE: vkInfo.vkDate ? vkInfo.vkDate : '',
      VK_TIME: vkInfo.vkTime ? vkInfo.vkTime : '',
      VK_MAC: vkInfo.vkMac ? vkInfo.vkMac : '',
      SessionHash: '',
    } as FormData;
    let url;
    switch (type) {
      case LinkType.GEODEET:
        data.GTL = 'Geodeedi töölaud';
        url = 'geodeet/j_spring_security_check';
        break;
      case LinkType.TEHNILINE:
        data.UC1 = 'Tehnilised tingimused';
        url = 'cgi-bin/tt_eteenindus.cgi';
        break;
      case LinkType.KOOSKOLASTAMINE:
        data.UC9 = 'Projektide kooskõlastamine';
        url = 'cgi-bin/tt_eteenindus.cgi';
        break;
      case LinkType.JARELVALVE:
        data.TLT = 'Järelevalve teenus';
        url = 'jrve/j_spring_security_check';
        break;
      default:
    }

    const form = document.createElement('form');
    form._submit_function_ = form.submit;

    form.setAttribute('method', 'POST');
    form.setAttribute('action', `${config.geopankUrl || 'https://geopank-arendus.cc.elion.ee'}/${url}`);

    for (const [key, value] of Object.entries(data)) {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', key);
      input.setAttribute('value', value);
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form._submit_function_();

    runInAction(() => {
      this.entryInformationHolder.status = FetchStatus.FETCHED;
    });
  };

  public loadEntryInformation = async () => {
    try {
      runInAction(() => {
        this.showNoAddressNotice = false;
        this.entryInformationHolder.status = FetchStatus.FETCHING;
      });
      const result = await API.get('/construction-portal/entry-information');
      runInAction(() => {
        this.entryInformationHolder = result.data;
        this.entryInformationHolder.status = FetchStatus.FETCHED;
        if (this.entryInformationHolder.referenceNumbers.length > 0) {
          this.setSelectedAddressOption({
            value: this.entryInformationHolder.referenceNumbers[0].refNumber,
            label: this.entryInformationHolder.referenceNumbers[0].address,
          });
        } else {
          this.showNoAddressNotice = true;
        }
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      runInAction(() => {
        this.entryInformationHolder.status = FetchStatus.ERROR;
      });
    }
  };
}

export default ConstructorPortalStore;
